import {
  Box,
  Button,
  Grid,
  InputLabel,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { FC, useEffect, useState } from "react";
import useResponsive from "../../hooks/useResponsive";
import { labelSty } from "../UIElements/styles-input";

import {
  InputCustomIcon,
  InputCustomSize,
  TextArea,
} from "../UIElements/input-ui";
import SelectInput from "../UIElements/select";
import { PostJobProps } from "../../interface/job_board";
import { CalenderIcon, LocationIcon } from "../UIElements/svg-icons";
import CustomDatePicker from "../UIElements/date-picker";
import { Dayjs } from "dayjs";
import { CustomRange } from "../UIElements/range";
import SelectInputMultiple, {
  MultiSelectBenefits,
} from "../UIElements/select-multiple";
import {
  departmentOptions,
  experienceLevel,
  jobTitle,
  nurseQualificationData,
  qualificationData,
} from "./data";
import DateRangeInput from "../UIElements/date-range";
import { documentButtonStyle } from "./styles";
import { getAllStates } from "../../utils/nigeria-states";

const JobApplication: FC<{
  jobDetails: PostJobProps;
  handleDocumentChange: (event: any) => void;
  document: string[];
  benefits: string[];
  handleBenefits: (event: any) => void;
  changeJobDetails: React.Dispatch<React.SetStateAction<PostJobProps>>;
  selectDate: Dayjs | null;
  changeSelectDate: (e: any) => void;
  type: string;
  typed_department: string;
  setTypedDepartment: (department: string) => void;
  setContractDuration: React.Dispatch<any>;
  nurses: string[];
  handleNurses: (e: any) => void;
}> = ({
  jobDetails,
  changeJobDetails,
  selectDate,
  changeSelectDate,
  document,
  type,
  benefits,
  handleBenefits,
  handleDocumentChange,
  typed_department,
  setTypedDepartment,
  setContractDuration,
  nurses,
  handleNurses,
}) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const isSmall = useResponsive("down", "sm", "xl");
  const [open_range, setOpen_range] = useState(false);
  const get_states = getAllStates();

  useEffect(() => {
    if (type === "full time" && !jobDetails?.employment_type) {
      changeJobDetails((prev) => ({
        ...prev,
        employment_type: "full time",
      }));
    }
  }, [type, jobDetails?.employment_type, changeJobDetails]);

  return (
    <Box sx={{}}>
      <Box>
        <Typography
          sx={{
            fontFamily: "Lexend",
            fontWeight: 500,
            color: theme.palette.neutral.light,
            my: 2,
          }}
          variant="subtitle2"
        >
          Job Details
        </Typography>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 2, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <SelectInput
              label={"Job Title"}
              value={jobDetails?.job_title ?? ""}
              onChange={(e) =>
                changeJobDetails((prev) => ({
                  ...prev,
                  job_title: e.target.value,
                }))
              }
              options={jobTitle}
            />
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            {jobDetails?.department != "Other" ? (
              <SelectInput
                label={"Department"}
                value={jobDetails?.department ?? ""}
                onChange={(e) =>
                  changeJobDetails((prev) => ({
                    ...prev,
                    department: e.target.value,
                  }))
                }
                options={departmentOptions}
              />
            ) : (
              <>
                <InputLabel sx={{ ...labelSty, mb: 2 }}>
                  Type your Department
                </InputLabel>

                <InputCustomSize
                  placeholder={"Type Department here"}
                  value={typed_department}
                  onchange={(e: any) => setTypedDepartment(e.target.value)}
                  type={"text"}
                  size={"medium"}
                  styles={undefined}
                />
              </>
            )}
          </Stack>
        </Stack>
        <Stack sx={{ width: "100%", my: 2 }}>
          <InputLabel sx={{ ...labelSty, mb: 2 }}>Job Description</InputLabel>
          <TextArea
            placeholder={
              "Provide a detailed description of the responsibilities, duties, and expectations for the role."
            }
            value={jobDetails?.job_description}
            onchange={(e: any) =>
              changeJobDetails((prev) => ({
                ...prev,
                job_description: e.target.value,
              }))
            }
          />
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 2, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            {jobDetails?.job_title === "Nurse" ? (
              <SelectInputMultiple
                label={"Qualifications"}
                value={nurses}
                disabled={false}
                onChange={handleNurses}
                options={nurseQualificationData}
              />
            ) : (
              <SelectInput
                label={"Qualifications"}
                value={jobDetails?.qualifications ?? ""}
                onChange={(e) =>
                  changeJobDetails((prev) => ({
                    ...prev,
                    qualifications: e.target.value,
                  }))
                }
                options={qualificationData}
              />
            )}
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <SelectInput
              label={"Experience Level"}
              value={jobDetails?.experience_level}
              onChange={(e) =>
                changeJobDetails((prev) => ({
                  ...prev,
                  experience_level: e.target.value,
                }))
              }
              options={experienceLevel}
            />
          </Stack>
        </Stack>
      </Box>

      {/*  */}
      <Box>
        <Typography
          sx={{
            fontFamily: "Lexend",
            fontWeight: 500,

            color: theme.palette.neutral.light,
            my: 0,
            mb: 2,
          }}
          variant="subtitle2"
        >
          Employment Type & Schedule
        </Typography>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 2, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <SelectInput
              label={"Employment Type"}
              value={jobDetails?.employment_type}
              onChange={(e) =>
                changeJobDetails((prev) => ({
                  ...prev,
                  employment_type: e.target.value,
                }))
              }
              options={
                type === "full time"
                  ? [{ value: "full time", label: "Full Time" }]
                  : [
                      { value: "short locum", label: "Short Locum" },
                      { value: "long locum", label: "Long Locum" },
                    ]
              }
            />
          </Stack>

          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <SelectInput
              label={"Work Schedule"}
              value={jobDetails?.work_schedule}
              onChange={(e) =>
                changeJobDetails((prev) => ({
                  ...prev,
                  work_schedule: e.target.value,
                }))
              }
              options={[
                { value: "Morning shift", label: "Morning shift" },
                { value: "Afternoon shift", label: "Afternoon shift" },
                { value: "Night shift", label: "Night shift" },
                { value: "On call", label: "On call" },
              ]}
            />
          </Stack>
        </Stack>

        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"start"}
          justifyContent={"space-between"}
          sx={{ my: 2, gap: 2 }}
        >
          {type === "temporary" && (
            <Stack sx={{ width: isMedium ? "100%" : "49%", my: 2 }}>
              <InputLabel sx={{ ...labelSty, mb: 0 }}>
                Contract Duration (For Temporary Jobs)
              </InputLabel>
              <DateRangeInput onChange={setContractDuration} />
            </Stack>
          )}
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <SelectInput
              label={"Work Type"}
              value={jobDetails?.work_type}
              onChange={(e) =>
                changeJobDetails((prev) => ({
                  ...prev,
                  work_type: e.target.value,
                }))
              }
              options={[{ value: "on-site", label: "On site" }]}
            />
          </Stack>
        </Stack>
      </Box>
      <Box>
        <Typography
          sx={{
            fontFamily: "Lexend",
            fontWeight: 500,

            color: theme.palette.neutral.light,
            my: 3,
            mb: 1,
          }}
          variant="subtitle2"
        >
          Compensation
        </Typography>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 2, gap: 2 }}
        >
          {/* <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <SelectInput
              label={"Select state location"}
              value={jobDetails?.work_location}
              onChange={(e: any) =>
                changeJobDetails((prev) => ({
                  ...prev,
                  work_location: e.target.value,
                }))
              }
              options={get_states}
            />
          </Stack> */}
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 2 }}>
              Salary Amount{" "}
              {type === "full time"
                ? "(monthly)"
                : jobDetails.employment_type === "short locum"
                ? "(daily)"
                : "(daily)"}
            </InputLabel>
            <InputCustomIcon
              icon={<></>}
              placeholder={"Enter Salary (naira)"}
              value={jobDetails?.salary}
              onchange={(e) =>
                changeJobDetails((prev) => ({
                  ...prev,
                  salary: e.target.value,
                }))
              }
              type={"number"}
              size={"medium"}
              styles={undefined}
            />
          </Stack>
        </Stack>
        <Stack sx={{ width: isMedium ? "100%" : "49%", mb: 4 }}>
          <InputLabel sx={{ ...labelSty, mb: 2 }}>
            Additional benefits (kindly select more than one or No benefit if
            there's none)
          </InputLabel>

          <MultiSelectBenefits
            benefits={benefits}
            handleBenefits={handleBenefits}
          />
        </Stack>
      </Box>
      <Box>
        <Typography
          sx={{
            fontFamily: "Lexend",
            fontWeight: 500,
            color: theme.palette.neutral.light,
            mt: 3,
            my: 2,
          }}
          variant="subtitle2"
        >
          Application Details
        </Typography>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ mb: 2, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>
              Application Deadline
            </InputLabel>
            <CustomDatePicker
              label=""
              value={selectDate}
              onChange={changeSelectDate}
            />
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <SelectInputMultiple
              label={
                "Required Documents (full registration, annual practice, and licence is required)"
              }
              value={document}
              disabled={false}
              onChange={handleDocumentChange}
              options={[
                { value: "Full_registration", label: "Full registration" },
                {
                  value: "Annual_practicing_license",
                  label: "Annual practicing license",
                },
                { value: "NYSC_discharge", label: "NYSC discharge" },
                { value: "resume", label: "Resume" },
                { value: "cover_letter", label: "Cover Letter" },
                {
                  value: "professional_certificate",
                  label: "Professional Certificate",
                },
                { value: "references", label: "References" },
              ]}
            />
          </Stack>
        </Stack>
        <Stack direction={"row"} sx={{ gap: 3, flexWrap: "wrap" }}>
          {document?.includes("Full_registration") && (
            <Button sx={documentButtonStyle}>Full registration</Button>
          )}
          {document?.includes("Annual_practicing_license") && (
            <Button sx={documentButtonStyle}>Annual practicing licence</Button>
          )}
          {document?.includes("NYSC_discharge") && (
            <Button sx={documentButtonStyle}>NYSC discharge</Button>
          )}
          {document?.includes("resume") && (
            <Button sx={documentButtonStyle}>Resume/CV</Button>
          )}
          {document?.includes("cover_letter") && (
            <Button sx={documentButtonStyle}>Cover Letter</Button>
          )}
          {document?.includes("professional_certificate") && (
            <Button sx={{ ...documentButtonStyle, width: "190px" }}>
              Professional Certifications
            </Button>
          )}
          {document?.includes("references") && (
            <Button sx={documentButtonStyle}>References</Button>
          )}

          {document?.includes("references") && (
            <Button sx={documentButtonStyle}>References</Button>
          )}
        </Stack>
        <Stack sx={{ width: "100%", my: 2 }}>
          <InputLabel sx={{ ...labelSty, mb: 2 }}>
            Any other instruction
          </InputLabel>
          <TextArea
            placeholder={
              "Provide  instructions, including email address or link."
            }
            value={jobDetails?.how_to_apply}
            onchange={(e: any) =>
              changeJobDetails((prev) => ({
                ...prev,
                how_to_apply: e.target.value,
              }))
            }
          />
        </Stack>
        <Stack sx={{ width: "100%", my: 2 }}>
          <InputLabel sx={{ ...labelSty, mb: 2 }}>
            External application link (optional)
          </InputLabel>
          <InputCustomSize
            placeholder={"https://example.com/apply"}
            value={jobDetails?.external_link ?? ""}
            onchange={(e: any) =>
              changeJobDetails((prev) => ({
                ...prev,
                external_link: e.target.value,
              }))
            }
            type={"url"}
            size={"medium"}
            styles={undefined}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default JobApplication;
